import {useState} from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import LockIcon from '@mui/icons-material/Lock';
import Stack from '@mui/material/Stack';

import {loadStripe} from '@stripe/stripe-js';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';

import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

import config from '../../config/config';
import {baseFonts} from "../../config/getTheme";
import {formatPatientName} from "../../helpers/patients";
import CustomTextField from '../common/CustomTextField';
import CustomInput from "../common/CustomInput";
import StaxPaymentForm from '../payments/StaxPaymentForm';
import * as formValidations from "../../helpers/validations";
import TitleDivider from "./TitleDivider";
import './PaymentForm.scss';

// https://stripe.com/docs/stripe-js/react
const stripePromise = loadStripe(config().stripePublicKey);

const elementStyle = {
  base: {
    fontSize: '13.5px',
    lineHeight: '1.406rem',
    fontFamily: [config().secondFont, ...baseFonts].join(',')
  }
};

const validations = {
  zipCode: str => formValidations.zip(str) && str.length > 0,
  nameOnCreditCard: str => formValidations.name(str) && str.length > 0,
};

const checkIfFormIsValid = (
  validationDict,
) => {
  return Object.keys(validationDict).reduce((acc, k) => (
    validationDict[k] ? acc : false
  ), true);
};

const handleSubmit = ({
  elements,
  stripe,
  onSubmit,
  setStripeError,
  setSubmitting
}) => (
  async ({promoCode, zipCode, nameOnCreditCard}) => {
    setSubmitting(true);
    if (!stripe || !elements) {
      return setStripeError({message: 'Stripe is not ready yet.'});
    }
    const cardNumberElement = elements.getElement(CardNumberElement);
    const cardCvcElement = elements.getElement(CardCvcElement);
    const {token, error} = await stripe.createToken(cardNumberElement, {
      address_zip: zipCode,
      cvc: cardCvcElement.value,
      name: nameOnCreditCard
    });
    if (error) {
      setSubmitting(false);
      return setStripeError({message: 'Sorry we are unable to connect this card. Please confirm all information and retry. If you continue to have difficulty, please contact us @ 202-681-1474'});
    }
    onSubmit({promoCode, stripeToken: token.id});
    setSubmitting(false);
  }
);

const handleFieldUpdate = (formValues, setFormValues) => fieldName => e => {
  const {value} = e.target;
  const values = {
    ...formValues,
    [fieldName]: fieldName === 'email' ? value.toLowerCase() : value,
  };
  return setFormValues(values);
};

const PaymentForm = props => {
  const {onSubmit,user} = props;
  const [submitting, setSubmitting] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);
  const [promoIsCollapsed, setPromoIsCollapsed] = useState(true);
  const [stripeError, setStripeError] = useState(null);
  const [cardNumberError, setCardNumberError] = useState(null);
  const [promoCode, setPromoCode] = useState(
    user && user.promo_code
    ? user.promo_code
    : null
  );
  const [formValues, setFormValues] = useState({
    zipCode: user?.address_zip || '', 
    nameOnCreditCard: user?.user?.first_name ? formatPatientName(user) : '',
  });

  const validationDict = Object.keys(validations).reduce((acc, k) => ({
    ...acc,
    [k]: validations[k](formValues[k])
  }), {});

  const onChangeHandler = handleFieldUpdate(formValues, setFormValues);

  const formIsValid = checkIfFormIsValid(
    validationDict,
  );

  return (
    <Stack
      className='PaymentForm'
      gap={0.25}
    >
      <TitleDivider 
        text='Enter Payment Information'
        Icon={LockIcon}
      />

      <Typography variant='large500Tight' style={{marginTop: '0.5rem', marginBottom: '1rem'}}>
        You will{' '}
        <span style={{textDecoration: 'underline', fontWeight: 'bold'}}>not</span>{' '}
        be charged until your first session is completed. Payments are secure and encrypted.
      </Typography>
      {
        !ready
          ? <Stack direction='row' justifyContent='center'>
            <CircularProgress className='PaymentForm__loading'/>
          </Stack>
          : null
      }
      <Stack width='100%' gap={0.5}>
        <CustomTextField
          className='PaymentForm__input PaymentForm__name-input'
          label='Name on Credit Card'
          variant='outlined'
          width='100%'
          required
          defaultValue={user?.user?.first_name ? formatPatientName(user) : ''}
          onChange={onChangeHandler('nameOnCreditCard')}
        />

        <CustomInput
          className='PaymentForm__input PaymentForm__card-input'
          label='Credit Card Number'
          helperText={cardNumberError}
          helperTextClass='PaymentForm__card-input-helper-text'
          required
        >
          <CardNumberElement
            className="PaymentForm__card-element"
            options={{ style: elementStyle, placeholder: '#### - #### - #### - ####' }}
            onChange={e => setCardNumberError(get(e, 'error.message') || null)}
            onReady={() => setReady(true)}
          />
        </CustomInput>
        <Grid container spacing={0.25}>
          <Grid item xs={12} md={4}>
            <CustomInput
              className='PaymentForm__input PaymentForm__expiry-input'
              label='Expiration'
              required
            >
              <CardExpiryElement
                className="PaymentForm__card-element"
                options={{ style: elementStyle }}
                onReady={() => setReady(true)}
              />
            </CustomInput>
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomInput
              className='PaymentForm__input PaymentForm__cvc-input'
              label='CVV'
              required
              fullWidth
              >
              <CardCvcElement
                className="PaymentForm__card-element"
                options={{ style: elementStyle }}
                />
            </CustomInput>
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomTextField
              className='PaymentForm__input PaymentForm__zip-input'
              label='Zip'
              variant='outlined'
              width='100%'
              required
              fullWidth
              type='number'
              minLength={5}
              defaultValue={user?.address_zip}
              onChange={onChangeHandler('zipCode')}
            />
          </Grid>
        </Grid>
      </Stack>

      {
        stripeError
          ? (
            <Alert className='PaymentForm__error' severity="error" icon={<span className='PaymentForm__error-icon'></span>}>
              <Typography variant='large500Tight' color='text.danger'>
                {stripeError.message}
              </Typography>
            </Alert>
          )
          : null
      }

      <Stack>
        <Button
          className='PaymentForm__promo-button'
          sx={{
            width: 'fit-content',
            height: '30px',
            alignSelf: 'flex-start',
            gap: '8px',
            display: 'flex',
            border: '2px solid var(--button-stroke)',
            borderRadius: '8px',
            padding: '8px 7px',
            marginTop: '12px'
          }}
          size='small'
          variant='primaryOutline'
          fullWidth={false}
          onClick={() => setPromoIsCollapsed(!promoIsCollapsed)}
        >
          <span className='PaymentForm__promo-button-icon'></span>
          <Typography variant='small' letterSpacing='0px'>
            Enter Promo Code
          </Typography>
        </Button>

        <Collapse in={!promoIsCollapsed}>
          <Box className='PaymentForm__promo-code-container' sx={{ width: '100%' }}>
            <Stack>
              <CustomTextField
                className='PaymentForm__promo-code-input'
                label='Promo Code'
                placeholder='ABC1234567'
                defaultValue={get(user, 'promo_code') || null}
                variant='outlined'
                width='100%'
                onChange={e => setPromoCode(get(e, 'target.value'))}
              />
            </Stack>
          </Box>
        </Collapse>
      </Stack>

      <Typography
        variant='large500Tight'
        fontStyle='italic'
        color='text.light'
        style={{paddingTop:'1rem'}}
      >
        By clicking continue, I recognize that MovementX will not submit payments to my insurance and will not accept
        reimbursement on my behalf.
      </Typography>

      <Button
        sx={{alignSelf: 'center'}}
        variant='primaryGradient'
        size='medium'
        disabled={submitting || !formIsValid}
        style={{marginTop:'1rem'}}
        onClick={() => handleSubmit({
          elements,
          stripe,
          onSubmit,
          setStripeError,
          setSubmitting
        })({ promoCode, zipCode: formValues.zipCode, nameOnCreditCard: formValues.nameOnCreditCard })}
      >
        Continue to Final Step
      </Button>
    </Stack>
  );
};


// Stripe forms and react functions must be wrapped in an Elements Provider.
const PaymentFormProvider = props => {
  const {stripeClientSecret} = props;
  if (props?.user?.payment_platform === 'stax') {
    return <StaxPaymentForm {...props} showOnboardingFields={true} />;
  }
  return (
    <Elements
      stripe={stripePromise}
      options={{clientSecret: stripeClientSecret}}
    >
      <PaymentForm {...props} />
    </Elements>
  );
};

PaymentFormProvider.propTypes = {
  stripeClientSecret: PropTypes.string, // secret key from the server
};

export default PaymentFormProvider;
